/**
 * The related items panel that appears on the overview screen.
 */
import $ from 'jquery'

import _ from 'lodash'
import Backbone from 'backbone'
import template from './related-items-list.hbs'
import i18n from 'scoreboard/components/i18n'
import sections from 'scoreboard/components/sections'
import RelatedItem from './related-item'
import RelatedItems from './related-items'
import conductor from 'scoreboard/components/conductor'
import DeleteConfirmation from 'scoreboard/components/delete-confirmation'
import appDisplay from 'scoreboard/components/app-display'
import permissions from 'scoreboard/components/permissions'
import Tooltip from 'scoreboard/components/tooltip'
import AddMenu from './add-menu'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import url from 'scoreboard/components/url'
import scoreboardUrl from 'scoreboard/components/scoreboard-url'
import highlight from 'scoreboard/components/highlight'
import WebAddressForm from '../components/web-address-form'
import linkify from 'scoreboard/components/linkify'
import Dataset from 'scoreboard/datasets/model/dataset'
import requiresOrg from 'scoreboard/components/requires-org'
import SyncedItemTooltip from 'scoreboard/components/synced-item-tooltip'
import icon from 'scoreboard/components/icon'
import InitiativeImpactDialog, {
	impactToMessage
} from './initiative-impact-dialog'
import impactIconId from './impact-icon-id'
import { SCORECARD_ITEM } from '../components/domain-type'

var RelatedItemsView = Backbone.View.extend({

	// this.model is a domain object of some sort (e.g. ScorecardNode)

	className: 'panel related-items',

	events: {
		'click .add-item': 'add',
		'click .delete': 'del',
		'click .edit': 'edit'
	},

	initialize: function() {
		this.collection = new RelatedItems(this.model.get('relatedItems'))
		this.collection.on('add remove', function() {
			this.trigger('change:relatedItems')
			// #23850 so Overview screens can be updated without chaining events
			// through several views
			Backbone.trigger('change:relatedItems', this.collection.toJSON(),
				this.model.get('type'))
		}.bind(this))

		// #23850 whenever related items change make sure we update the display
		this.listenTo(Backbone, 'change:relatedItems', (relatedItems, type) => {
			// #25828 only update if this relatedItemsList is for the same model type
			// (this prevents related item changes made for a briefing book from
			// being displayed on a briefing slide's related items list)
			if (type !== this.model.get('type')) {
				return
			}
			this.collection.reset(relatedItems)
			this.render()
		})

		this.listenTo(this.model, 'change:relatedItems', () => {
			this.collection.reset(this.model.get('relatedItems'))
		})
	},

	render: function() {
		var items = this.collection.toJSON()
		items.forEach(formatItem.bind(this))

		const isLinked = this.model.get('type') === 'linked' &&
			this.model.get('useSourceRelatedItems')
		this.$el.html(template({
			items: items,
			msg: i18n.get('relatedItems.none'),
			showDelete: !isLinked
		}))

		// after emptying view need to re-delegate events
		this.secure()
		this.delegateEvents(this.events)
		// If list is re-rendered by adding a new item or switching periods, remove
		// the old tooltips.
		if (!_.isNil(this.covertButtonTooltips)) this.covertButtonTooltips.cleanup()
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		// if replicated make read only
		if (!_.isNil(this.model.get('syncId'))) {
			this.$('.add-item, .delete, .edit').addClass('is-not-displayed')
		}

		// If related item from a template, add a tooltip explaining why it can't
		// be edited or deleted.
		this.syncedItemTooltips = []
		_.forEach(this.$('.synced-item-button'), el => {
			this.syncedItemTooltips.push(new SyncedItemTooltip({
				el: el,
				attachTo: $(el).find('.button'),
				itemType: i18n.get('relatedItems.item')
			}).render())
		})

		items.forEach(i => this.renderImpactIcon(i))

		return this
	},

	secure: function() {
		let hasPermission = requiresOrg(this.model.section) ?
			permissions.relatedItems(this.model.get('organizationId')) :
			permissions.anyRelatedItems()
		let hideControls = this.model.get('pendingDeletion') || !hasPermission
		if (hideControls) {
			this.$('.add-item, .delete, .edit').hide()
		}
	},

	add: function() {
		var relatedItem = new RelatedItem(this._getSourceAttributes())

		// we must wait for the server's response so we know the ID
		relatedItem.once('sync', function(model, response) {
			// The related-item model attributes are relative to the current object, whereas the
			// RelatedItemsController needs more generic attributes (ex. id1, id2, type1, type2).
			// So translate to an appropriate model before adding it to the collection
			this.collection.add({
				id: model.get('id'),
				label: model.get('label'),
				subLabel: model.get('subLabel'),
				organization: model.get('organization'),
				relatedId: model.get('id2'),
				type: model.get('type2'),
				color: model.get('color'),
				// #15212 the tree node doesn't always have the subtype populated, but the
				// response from adding the related item does
				subtype: response.subtype,
				url: model.get('url'),
				impactCalculations: response.impactCalculations
			})
		}.bind(this))

		relatedItem.once('request', function () {
			this.addTooltip && this.addTooltip.remove()
		}.bind(this))

		var addMenu = new AddMenu({
			model: relatedItem,
			collection: this.collection
		})

		var addButton = this.$('.add-item')

		this.addTooltip = new Tooltip({
			attachTo: addButton,
			content: addMenu,
			placement: 'below',
			arrow: 'right',
			extraClass: 'add-related-item has-bootslide-menu'
		}).render()
	},

	// returns the id of the related item corresponding to the given event
	relatedItemId: function(e) {
		return this.$(e.target).closest('[data-id]').data('id')
	},

	del: function(e) {
		if (isDisabledClick(e)) {
			return
		}

		var model = this.collection.get(this.relatedItemId(e))
		var related = highlight(model.get('label'))
		// this isn't used with the new resource bundle message, but the old
		// message had a second argument and it won't hurt to specify it for the
		// new message
		var domainObject = highlight(this.model.get('label'))
		var message = i18n.get('relatedItems.delete.relationship.prompt',
				related, domainObject)
		var confirmation = new DeleteConfirmation({ message: message })
		appDisplay.dialog(confirmation)
		confirmation.once('confirm', function() {
			model.once('destroy', function(model) {
				this.collection.remove(model)
				confirmation.trigger('done')
			}.bind(this)).destroy()
		}.bind(this))
	},

	edit: function(e) {
		if (isDisabledClick(e)) {
			return
		}

		var target = $(e.currentTarget)
		var relatedItem = this.collection.get(this.relatedItemId(e))
		relatedItem.set(this._getSourceAttributes())

		var webAddressForm = new WebAddressForm({
			header: i18n.get('relatedItems.webAddress'),
			primaryButtonLabel: i18n.get('common.save'),
			label: relatedItem.get('label'),
			url: relatedItem.get('url')
		})
		this.editTooltip = new Tooltip({
			attachTo: target.closest('.edit'),
			content: webAddressForm,
			placement: 'below',
			arrow: 'right',
			extraClass: 'edit-related-item has-bootslide-menu'
		}).render()
		// Manually keep the .edit button visible so that the tooltip can remain attached
		target.closest('.list-item-actions').css('display', 'block')
		const remove = () => {
			target.closest('.list-item-actions').removeAttr('style')
			_.invoke(this.editTooltip, 'remove')
		}
		webAddressForm.once('submit', ({label, url}) => {
			// #27758 the link id is needed for saving the change
			let id2 = relatedItem.get('relatedId')
			relatedItem.save({label, url, id2}, {
				success: () => {
					this.collection.add(relatedItem, {merge: true})
					this.render()
				}
			})
		})
		webAddressForm.once('cancel', remove)
		relatedItem.once('request', remove)
		this.editTooltip.once('remove', remove)
	},

	_getSourceAttributes: function() {
		return {
			id1: this.model.id,
			type1: sections.toType(this.model.section),
			organizationId1: this.model.get('organizationId'),
			projectId1: this.model.get('projectId')
		}
	},

	renderImpactIcon: function (item) {
		let calcs = item.impactCalculations
		if (!calcs || _.includes([item.subtype, this.model.get('type')], 'milestone')) {
			return
		}

		let impact = item.impactCalculations.impact
		let initiativeType = _.capitalize(item.impactCalculations.initiative.type)

		let buttonIcon = icon(impactIconId(impact, true))
		let tooltipText = impactToMessage(impact, initiativeType)

		let button = $(`<li class="impact-item"><a class="impact button button-covert" data-action="${tooltipText}">${buttonIcon}</a></li>`)
		this.$(`.related-item[data-id=${item.id}]`)
			.addClass('has-two-action-buttons')
			.find('.list-item-actions')
			.prepend(button)

		button.on('click', () => this.openImpactDialog(item))
	},

	openImpactDialog: function (relatedItem) {
		let view = new InitiativeImpactDialog({
			calculations: relatedItem.impactCalculations
		})

		appDisplay.dialog(view)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.covertButtonTooltips, this.addTooltip, this.editTooltip], 'remove')
		_.invokeMap(this.syncedItemTooltips, 'remove')
	}

})

/**
 * Given an object containing data for a related item, populates the object with attributes used to display the
 * item to the user (e.g. icon, sublabel, the href to use, etc...).  Note, the object passed is modified
 *
 * @param item A object containing data about a related item
 */
function formatItem (item = null) {
	if (!item) return

	if (item.type === Dataset.DATASET) {
		// subtype is okay to use for datasets, but we need to map 'view' to 'dataset-view'
		item.icon = item.subtype === 'view' ? 'dataset-view' : item.subtype
	} else if (item.type === 'briefing' && item.subtype === 'briefing') {
		// Ensure we're using the correct verbiage for our briefings icon
		item.icon = 'briefings'
	} else if (item.type === 'strategy-map' && item.subtype === 'dashboard') {
		// strategy maps and dashboards share the same subtype enum; so a subtype of
		// 'dashboard' for a strategy map should have the strategy map icon
		item.icon = 'strategy-map'
	} else if (item.type === 'file') {
		item.icon = 'document-blank'
	} else if (item.type === SCORECARD_ITEM && item.subtype == 'root') {
		item.icon = 'scorecard' // #59522
	} else {
		// scorecard items and initiatives will have a subtype (ex 'metric', 'task')
		// so use that instead of the type
		item.icon = item.subtype ? item.subtype : item.type
	}

	item.external = item.type === 'web-address'

	let label = _.get(item, 'organization.label')
	if (item.type === 'web-address') {
		label = url.hostname(item.url)
	} else if (item.type === Dataset.DATASET) {
		if (item.subtype === Dataset.DATASET) {
			label = i18n.get('relatedItems.datasets.tracks', _.get(item, 'subLabel', ''))
		} else {
			// #24919 use root dataset name as sublabel for dataset folders and views
			label = _.get(item, 'subLabel', '')
		}
	}
	item.subLabel = label

	if (item.external) {
		item.href = linkify(item.url)
	} else {
		// for internal links, add the baseUrl so "open in new tab", cmd+click, etc work properly
		item.href = scoreboardUrl(conductor.overviewUrl(sections.fromType(item.type), item.relatedId))
	}

	item.iconClass = item.icon + (item.color ? (' ' + item.color) : '')
}

function isDisabledClick (e) {
	return $(e.target).hasClass('is-disabled')
}

export default RelatedItemsView
export { formatItem }

RelatedItemsView.__filename__ = 'related-items/related-items-list.js'
